
import { Link } from "react-router-dom";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Phone, Mail, MapPin, CheckCircle2, Users, Award, Clock, Heart, Shield } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
export default function CriticalCarePage() {
  const { open: openAppointment } = useAppointment();
  const doctors = [];


  const subspecialties = [
    {
      title: "Medical ICU (MICU)",
      description: "For severe infections, respiratory failure, heart failure & complex medical emergencies.",
      image: "/MedicalICU(MICU).jpg",
    },
    {
      title: "Surgical ICU (SICU)",
      description: "Post-operative monitoring & support after major surgeries.",
      image: "/SurgicalICU(SICU).jpg",
    },
    {
      title: "Cardiac ICU (CICU)",
      description: "Acute cardiac conditions, arrhythmias & heart failure care.",
      image: "/CardiacICU(CICU).jpg",
    },
    {
      title: "Neuro ICU",
      description: "Management of stroke, brain injuries & neurological crises.",
      image: "/NeuroICU.jpg",
    },
    {
      title: "Trauma ICU",
      description: "Polytrauma, accident, and high-impact injury stabilization.",
      image: "/TraumaICU.jpg",
    },
    {
      title: "Respiratory Critical Care",
      description: "Ventilator support, ARDS management & lung failure treatment.",
      image: "/RespiratoryCriticalCare.jpg",
    },
  ];

  const treatments = [
    "24x7 Intensive Care Monitoring",
    "Advanced Ventilator & Life Support Systems",
    "Invasive & Non-Invasive Ventilation",
    "Hemodynamic Monitoring & Organ Support",
    "Sepsis & Septic Shock Management",
    "Post-Operative Critical Care",
    "Dialysis Support for Kidney Failure (CRRT/HD)",
    "Emergency Resuscitation & Code Blue Response",
    "Trauma & Multi-Organ Failure Management",
  ];

  const whyChoose = [
    {
      icon: Clock,
      title: "24x7 Staffed ICUs",
      description: "Intensive Care Units with experienced critical care specialists round-the-clock",
    },
    {
      icon: Award,
      title: "Advanced Life-Support",
      description: "Advanced life-support systems, ventilators & monitoring technology",
    },
    {
      icon: Users,
      title: "Multidisciplinary Team",
      description: "Team including intensivists, anesthetists, surgeons & specialists",
    },
    {
      icon: Heart,
      title: "Rapid Emergency Response",
      description: "Rapid response to emergencies & critical events with immediate action",
    },
    {
      icon: Shield,
      title: "Infection Control",
      description: "Infection-controlled ICU environment for maximum patient safety",
    },
  ];

  const articles = [
    {
      title: "What Is Critical Care? Understanding ICU Treatment",
      excerpt:
        "Learn about intensive care units, when critical care is needed, and what to expect during ICU treatment for life-threatening conditions.",
    },
    {
      title: "How Ventilators Support Patients During Respiratory Failure",
      excerpt:
        "Understanding mechanical ventilation, its role in critical care, and how ventilators help patients breathe during severe respiratory conditions.",
    },
    {
      title: "Sepsis: Early Warning Signs You Should Never Ignore",
      excerpt:
        "Recognize the early symptoms of sepsis and septic shock, and understand why immediate medical attention is crucial for survival.",
    },
    {
      title: "Why Multidisciplinary Care Matters in the ICU",
      excerpt:
        "Discover how collaborative care from intensivists, specialists, and nurses improves outcomes for critically ill patients.",
    },
  ];

  const faqs = [
    {
      question: "When does a patient need ICU care?",
      answer:
        "ICU care is required for life-threatening conditions such as severe infections, respiratory failure, trauma, strokes, cardiac arrest & multi-organ failure. Patients who need continuous monitoring, advanced life support, or mechanical ventilation are typically admitted to the ICU.",
    },
    {
      question: "Do you have ventilator and life-support systems?",
      answer:
        "Yes, our ICU is equipped with the latest ventilators, monitoring devices & advanced life-support technology. We have both invasive and non-invasive ventilation options, along with hemodynamic monitoring, dialysis support, and other critical care equipment.",
    },
    {
      question: "Is critical care available 24x7?",
      answer:
        "Yes, the ICU team is available round-the-clock with dedicated intensivists and trained nursing staff. We provide 24/7 critical care monitoring and emergency response services, ensuring that critically ill patients receive immediate expert attention at any time.",
    },
  ];
  const schema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "MAMS Hospitals Critical Care Department",
    "url": "https://mamshospitals.com/critical-care",
    "logo": "https://mamshospitals.com/MAMS-Logo.png",
    "image": "https://mamshospitals.com/CriticalCare.jpg",
    "description": "MAMS Hospitals provides advanced critical care and ICU services in Hyderabad including ventilator support, trauma ICU, cardiac ICU, neuro ICU, sepsis management, and emergency life support.",
    "medicalSpecialty": "Critical Care Medicine",
    "telephone": "+91-7399999981",
    "priceRange": "$$",

    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500090",
      "addressCountry": "IN"
    },

    "availableService": [
      {
        "@type": "MedicalProcedure",
        "name": "24x7 Intensive Care Monitoring"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Ventilator & Life Support Systems"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Cardiac ICU & Neuro ICU Care"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Sepsis & Septic Shock Management"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Trauma & Multi-Organ Failure Management"
      }
    ],

    "sameAs": [
      "https://www.facebook.com/MAMSHospitals",
      "https://www.instagram.com/mams_hospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Is ICU care available 24x7?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals provides 24x7 intensive care and emergency monitoring with experienced intensivists and ICU specialists."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide ventilator support?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, our ICU is equipped with advanced ventilators, invasive and non-invasive ventilation systems, and life-support technologies."
        }
      },
      {
        "@type": "Question",
        "name": "Do you handle trauma and emergency ICU cases?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals provides trauma ICU, cardiac ICU, neuro ICU, and emergency critical care for severe medical emergencies."
        }
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Critical Care Department - MAMS Hospitals",
    "url": "https://mamshospitals.com/critical-care",
    "description": "Best critical care hospital in Hyderabad offering ICU care, ventilator support, trauma ICU, cardiac ICU, neuro ICU, sepsis management, and emergency life support.",
    "keywords": [
      "Critical Care Hospital Hyderabad",
      "Best ICU Hospital Hyderabad",
      "Ventilator Support Hyderabad",
      "Trauma ICU Hyderabad",
      "Cardiac ICU Hyderabad",
      "Neuro ICU Hyderabad",
      "Critical Care Hospital Bachupally"
    ]
  }
];

  return (
    <>
   <SEO
    title="Best Critical Care Hospital in Hyderabad | ICU & Life Support | MAMS Hospitals"
    description="MAMS Hospitals offers advanced critical care and ICU services in Hyderabad including ventilator support, cardiac ICU, neuro ICU, trauma ICU, sepsis management, and emergency life support."
    keywords="critical care hospital Hyderabad,
best ICU hospital Hyderabad,
ventilator support Hyderabad,
trauma ICU Hyderabad,
cardiac ICU Hyderabad,
neuro ICU Hyderabad,
intensive care unit Hyderabad,
life support hospital Hyderabad,
sepsis treatment Hyderabad,
emergency ICU Hyderabad,
multi organ failure treatment Hyderabad,
critical care hospital Bachupally,
MAMS Hospitals Critical Care"
    schema={schema}
  />
    <div className="flex flex-col min-h-screen">

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 md:py-24">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Best Critical Care Hospital in Hyderabad
              </div>

              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                Expert Critical Care When It Matters Most
              </h1>

              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                MAMS Hospitals is recognized as one of the Best Critical Care Hospitals in Hyderabad, offering advanced
                intensive care support for critically ill patients with state-of-the-art ICU facilities and expert
                multidisciplinary teams.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-teal-600 text-white p-6 rounded-2xl shadow-xl z-10">
                <div className="text-3xl font-bold">24/7</div>
                <div className="text-sm">ICU Care</div>
              </div>

              <img
                src="/CriticalCare.jpg"
                alt="Critical Care at MAMS Hospital"
                className="rounded-2xl shadow-2xl w-full"
              canonicalUrl="https://mamshospitals.com/departments/critical-care"
      />
            </div>
          </div>
        </div>
      </section>

      {/* Sub-Specialties Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Sub-Specialties in Critical Care</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Specialized intensive care units for diverse critical conditions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((specialty, index) => (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-300 border-gray-200 hover:border-teal-500"
              >
                <CardContent className="p-0">
                  <div className="relative h-48 overflow-hidden rounded-t-lg">
                    <img
                      src={specialty.image || "/placeholder.svg"}
                      alt={specialty.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{specialty.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{specialty.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Treatments */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Treatments & Critical Care Services</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive life-saving interventions and monitoring
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {treatments.map((treatment, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
                <span className="text-gray-700 font-medium">{treatment}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose MAMS Hospitals for Critical Care
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Life-saving expertise with advanced technology and compassionate care
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-gray-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                      <Icon className="h-7 w-7 text-teal-600" />
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-0 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Get answers to common questions about critical care</p>
          </div>

          <Accordion type="single" collapsible className="bg-white rounded-lg shadow-sm">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left px-6 hover:text-teal-600">
                  {faq.question}
                </AccordionTrigger>

                <AccordionContent className="px-6 text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department </p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
    </>
  );
}
